
import { useState, useEffect } from 'react';
import { get, set } from 'idb-keyval';
import { WorkItem, ReferenceAsset, AppSettings, ImageResolution, AspectRatio } from '../types';

export function usePersistence() {
  const [settings, setSettings] = useState<AppSettings>(() => {
    const saved = localStorage.getItem('appSettings');
    const defaults: AppSettings = {
      imageModel: 'gemini-2.5-flash-image' as const,
      concurrency: 20,
      apiProvider: 'google',
      grsaiHost: 'https://grsai.dakka.com.cn',
      googleBaseUrl: '', // Default to empty (official endpoint)
      googleApiKey: '',
      grsaiApiKey: '',
      siliconflowApiKey: ''
    };

    if (saved) {
      const parsed = JSON.parse(saved);
      // Merge saved settings with defaults to ensure new fields exist
      return { ...defaults, ...parsed };
    }
    return defaults;
  });

  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [assets, setAssets] = useState<ReferenceAsset[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [resolution, setResolution] = useState<ImageResolution>('1K');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('9:16');
  const [batchSize, setBatchSize] = useState<1 | 4 | 9>(1);

  useEffect(() => {
    const loadState = async () => {
      try {
        const savedAssets = await get('genstudio_assets');
        if (savedAssets && Array.isArray(savedAssets)) {
          setAssets(savedAssets.map((a: ReferenceAsset) => ({
            ...a, url: `data:${a.mimeType};base64,${a.base64}`
          })));
        }
        const savedWorkItems = await get('genstudio_workItems');
        if (savedWorkItems && Array.isArray(savedWorkItems)) setWorkItems(savedWorkItems);

        const savedRes = localStorage.getItem('genstudio_resolution');
        if (savedRes) setResolution(savedRes as ImageResolution);

        const savedRatio = localStorage.getItem('genstudio_aspectRatio');
        if (savedRatio) setAspectRatio(savedRatio as AspectRatio);

        const savedBatch = localStorage.getItem('genstudio_batchSize');
        if (savedBatch) setBatchSize(Number(savedBatch) as any);
      } catch (e) { console.error("Persistence layer recovery failed:", e); }
      finally { setHasLoaded(true); }
    };
    loadState();
  }, []);

  useEffect(() => {
    if (!hasLoaded) return;
    const assetsToSave = assets.map(a => ({ ...a, url: '' }));
    set('genstudio_assets', assetsToSave).catch(e => console.error("Save assets failed", e));
  }, [assets, hasLoaded]);

  useEffect(() => {
    if (!hasLoaded) return;
    set('genstudio_workItems', workItems).catch(e => console.error("Save workItems failed", e));
  }, [workItems, hasLoaded]);

  useEffect(() => {
    if (!hasLoaded) return;
    localStorage.setItem('appSettings', JSON.stringify(settings));
    localStorage.setItem('genstudio_resolution', resolution);
    localStorage.setItem('genstudio_aspectRatio', aspectRatio);
    localStorage.setItem('genstudio_batchSize', String(batchSize));
  }, [settings, resolution, aspectRatio, batchSize, hasLoaded]);

  return {
    settings, setSettings,
    workItems, setWorkItems,
    assets, setAssets,
    hasLoaded,
    resolution, setResolution,
    aspectRatio, setAspectRatio,
    batchSize, setBatchSize
  };
}
